import { editAdmin } from "@/services/permission/adminList";
import { outLogin } from "@/services/user/login";
import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { history, useModel } from "@umijs/max";
import type { MenuProps } from "antd";
import { Button, Form, Input, message, Modal, Spin } from "antd";
import { createStyles } from "antd-style";
import React from "react";
import { flushSync } from "react-dom";
import HeaderDropdown from "../HeaderDropdown";

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel("@@initialState");
  const { currentUser } = initialState || {};
  const [editPassword, setEditPassword] = React.useState(false);
  const [form] = Form.useForm();
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span className="anticon">
          {currentUser?.nickname || currentUser?.user_name}
        </span>
        <Button
          color="blue"
          variant="text"
          style={{ padding: "0" }}
          onClick={() => {
            setEditPassword(true);
            form.resetFields();
          }}
        >
          修改密码
        </Button>
      </div>
      <Modal
        open={editPassword}
        onCancel={() => {
          setEditPassword(false);
        }}
        width={400}
        closeIcon={false}
        footer={null}
      >
        <Form
          form={form}
          onFinish={async (values) => {
            const { newPassword } = values;
            try {
              const res = await editAdmin(initialState?.currentUser?.id || 0, {
                password: newPassword,
              });
              if (res.code === 0) {
                message.success("修改成功");
                setEditPassword(false);
              }
            } catch (error) {
              message.error("修改失败");
            }
          }}
        >
          <Form.Item
            name="newPassword"
            label="输入新密码"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            rules={[
              {
                required: true,
                message: "请输入新密码",
              },
              {
                min: 8,
                max: 8,
                message: "密码长度 8 位字符",
              },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%])[A-Za-z\d!@#$%]{8}$/,
                message: "密码组成 大小写字母、数字和1个特殊字符(!@#$%)",
              },
            ]}
          >
            <Input.Password maxLength={8} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="重复密码"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            dependencies={["newPassword"]}
            rules={[
              {
                required: true,
                message: "请输入新密码",
              },
              {
                min: 8,
                max: 8,
                message: "密码长度 8 位字符",
              },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%])[A-Za-z\d!@#$%]{8}$/,
                message: "密码组成 大小写字母、数字和1个特殊字符(!@#$%)",
              },
              {
                // 校验两次密码是否一致
                validator: (_, value) => {
                  const { newPassword } = form.getFieldsValue();
                  if (value && newPassword && value !== newPassword) {
                    return Promise.reject(new Error("两次密码不一致"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password maxLength={8} />
          </Form.Item>
          <Form.Item style={{ textAlign: "right" }}>
            <Button
              type="default"
              onClick={() => {
                setEditPassword(false);
                form.resetFields();
              }}
              style={{ marginRight: "8px" }}
            >
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              确定
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );

  // 新账号体系里面得nickname
  // return (
  //   <span className="anticon">
  //     {currentUser?.nickname || currentUser?.user_name}
  //   </span>
  // );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: "flex",
      flexDirection: "column",
      height: "48px",
      marginLeft: "auto",
      overflow: "hidden",
      alignItems: "center",
      padding: "0 8px",
      cursor: "pointer",
      borderRadius: token.borderRadius,
      "&:hover": {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await outLogin();
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get("redirect");
    // Note: There may be security issues, please note
    if (window.location.pathname !== "/user/login" && !redirect) {
      history.replace({
        pathname: "/user/login",
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel("@@initialState");

  const onMenuClick: MenuProps["onClick"] = (event) => {
    const { key } = event;
    if (key === "logout") {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.nickname) {
    // 新注册体系里面的nickname
    // if (!currentUser || !currentUser.nickname) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: "center",
            icon: <UserOutlined />,
            label: "个人中心",
          },
          {
            key: "settings",
            icon: <SettingOutlined />,
            label: "个人设置",
          },
          {
            type: "divider" as const,
          },
        ]
      : []),
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
